import { Box } from "@mui/material";
import * as moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { PreviewToggleButton } from "source/components/framework/Button/PreviewToggleButton";
import { AppUtils } from "source/framework/appUtils";
import { ICalendarTaskItem } from "ui/Scripts/source/models/calendarTask/calendarTaskItem";
import { ISessionState } from "source/models/session/sessionState";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import * as calendarTaskService from "../../../models/services/calendarTaskService";
import { ActionContainer } from "../../framework/ActionContainer";
import { AddButton } from "../../framework/Button/AddButton";
import { SettingsButton } from "../../framework/Button/SettingsButton";
import { Calendar, CalendarRefProps, ICalendarItem } from "../../framework/Calendar";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListSearchContainer } from "../../framework/List/ListSearchContainer";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { PageTitle } from "../../framework/Page/PageTitle";
import { CalendarTaskEditDialog } from "../CalendarTaskEditDialog";
import { CalendarTaskList } from "../CalendarTaskList";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { getCalendarTaskPageGeneralFilterFromFilterState, ICalendarTaskPageFilterState, ICalendarTaskPageGeneralFilter } from "./CalendarTaskPageUtils";
import { IListFilterItem } from "source/models/common/listFilterItem";

interface ICalendarTaskPageCalendarProps extends ICalendarTaskPageFilterState {
    companyId: string;
    startDate: number;
    endDate: number;
    savedFilter: IListFilterItem;
    initialItems: ICalendarTaskItem[];
    initialPrevItem: ICalendarTaskItem;
    initialNextItem: ICalendarTaskItem;
    settingsVisible: boolean;
    reportTabs: React.ReactNode;
    sessionState: ISessionState;
    showDocumentPreview: boolean;
    viewFilterIsSet: boolean;
    onToggleSettingsDialog: () => void;
    onOpenFilterDrawer: () => void;
    onChangeShowDocumentPreview: (showDocumentPreview: boolean) => void;
}

interface ICalendarTaskViewSearchFilter extends ICalendarTaskPageGeneralFilter {
    startDate: number;
    endDate: number;
    filter: string;
}

interface ICalendarTaskViewState extends ICalendarTaskViewSearchFilter {
    initialized: boolean;
    isLoading: boolean;
    showEditDialog: boolean;
    editDialogId: string;
    editDialogStartDate: number;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
    items: ICalendarTaskItem[];
    prevPrediodLastItem: ICalendarTaskItem;
    nextPrediodFirstItem: ICalendarTaskItem;
    calendarItems: ICalendarItem[];
    selectedId: string;
}

const getFromattedDate = (item:ICalendarTaskItem) => {
    return !item.endDate
        ? `${Base.timeToDateStr(item.startDate)} (${item.allDay ? Translations.AllDay : Base.timeToTimeStr(item.startDate)})`
        : Base.timeToDateStr(item.startDate) === Base.timeToDateStr(item.endDate)
            ? `${Base.timeToDateStr(item.startDate)} (${Base.timeToTimeStr(item.startDate)} - ${Base.timeToTimeStr(item.endDate)})`
            : `${Base.timeToDateStr(item.startDate)} (${Base.timeToTimeStr(item.startDate)}) - ${Base.timeToDateStr(item.endDate)} (${Base.timeToTimeStr(item.endDate)})`;
};

const getCalendarItems = (items: ICalendarTaskItem[]): ICalendarItem[] =>
    items.map((item) => ({
        id: item.id,
        companyId: item.companyId,
        recordId: item.recordId,
        databaseRecordType: item.databaseRecordType,
        title: item.title,
        start: moment.utc(item.startDate).toISOString(),
        end: item.endDate ? moment.utc(item.endDate).toISOString() : null,
        color: item.categoryColor,
        allDay: item.allDay,
        editable: true,
        location: item.locationText,
        formatedDate: getFromattedDate(item),
        parentId: item.parentId,
        extendedProps: { "private": item.isPrivate }
    }));

export const CalendarTaskPageCalendar = (props: ICalendarTaskPageCalendarProps) => {
    const calendarRef = useRef<CalendarRefProps>(null);

    const [state, setState] = useState<ICalendarTaskViewState>(() => {
        const generalFilterValues = getCalendarTaskPageGeneralFilterFromFilterState(props);
        return {
            ...generalFilterValues,
            initialized: false,
            isLoading: false,
            showEditDialog: false,
            editDialogId: "",
            editDialogStartDate: null,
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            items: [],
            startDate: props.startDate,
            endDate: props.endDate,
            filter: "",
            calendarItems: getCalendarItems(props.initialItems),
            prevPrediodLastItem: props.initialPrevItem,
            nextPrediodFirstItem: props.initialNextItem,
            selectedId: null,
            callback: null
        };
    });

    const searchItems = async (searchFilter: ICalendarTaskViewSearchFilter): Promise<ICalendarItem[]> => {
        setState((old) => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(() => calendarTaskService.getCalendarTaskItems(props.companyId, searchFilter.startDate, searchFilter.endDate, searchFilter.filter,
            searchFilter.states, searchFilter.categoryIds, searchFilter.handlerUserGroupCodes, searchFilter.privacy, searchFilter.createUserCodes), false, false, () => setState((old) => ({ ...old, isLoading: false })));
        const index = result.items.findIndex(i => i.id === state.selectedId);
        const calendarItems = getCalendarItems(result.items);
        setState((old) => ({
            ...old,
            ...searchFilter,
            initialized: true,
            startDate: result.startDate,
            endDate: result.endDate,
            items: result.items,
            prevPrediodLastItem: result.prevItem,
            nextPrediodFirstItem: result.nextItem,
            calendarItems,
            selectedId: index < 0 ? "" : state.selectedId
        }));
        return calendarItems;
    };
    
    useEffect(() => {
        setState((old) => ({
            ...old,
            startDate: props.startDate,
            endDate: props.endDate
        }));
    }, [props.startDate, props.endDate]);

    useEffect(() => {
        if (!props.initialItems) return;
        const calendarItems = getCalendarItems(props.initialItems);
        setState((old) => ({
            ...old,
            initialized: true,
            items: props.initialItems,
            prevPrediodLastItem: props.initialPrevItem,
            nextPrediodFirstItem: props.initialNextItem,
            calendarItems,
        }));
    }, [props.initialItems, props.initialPrevItem, props.initialNextItem]);

    useEffect(() => {
        if (!state.initialized) return;
        searchItems(state);
    }, [props.companyId]);

    useEffect(() => {
        if (!state.initialized) return;
        const generalFilterValues = getCalendarTaskPageGeneralFilterFromFilterState(props);
        setState((old) => ({
            ...old,
            ...generalFilterValues
        }));
        searchItems({ ...state, ...generalFilterValues });
    }, [props.selectedStateIds.join("#"), props.selectedCategoryIds.join("#"), props.selectedHandlerUserGroupCodes.join("#"), props.selectedPrivacy, props.selectedCreateUserCodes.join("#")]);
    
    useEffect(() => {
        if (!state.items) return;
        const index = state.editDialogId ? state.items.findIndex(i => i.id === state.editDialogId) : -1;
        setState((old) => ({
            ...old,
            editDialogHasPrevious: state.editDialogId ? index > 0 || !!state.prevPrediodLastItem : false,
            editDialogHasNext: state.editDialogId ? index < state.items.length - 1 || !!state.nextPrediodFirstItem : false
        }));
    }, [state.items, state.editDialogId]);

    const handleRefreshCalendar = async () => {
        await searchItems(state);
    };

    // #region Edit dialog
    const handleAddCalendarTask = (date: number) => {
        setState((old) => ({
            ...old,
            showEditDialog: true,
            editDialogId: Base.emptyGuid,
            editDialogStartDate: date,
            editDialogHasPrevious: false,
            editDialogHasNext: false
        }));
    };

    const handleCalendarDateClick = (date: number) => {
        handleAddCalendarTask(date);
    };
    
    const handleOpenCalendarItem = (id: string) => {
        setState((old) => ({
            ...old,
            showEditDialog: true,
            editDialogId: id,
            editDialogStartDate: null
        }));
    };

    const handleOkEditDialog = (id: string) => {
        searchItems(state);
    };

    const handleNewSavedEditDialog = (id: string) => {
        const stateChange = {
            editDialogId: id
        };
        setState((old) => ({
            ...old,
            ...stateChange
        }));
        searchItems({ ...state, ...stateChange });
    };

    const handleCloseEditDialog = () => {
        const stateChange = {
            showEditDialog: false,
            editDialogId: ""
        };
        setState((old) => ({
            ...old,
            ...stateChange
        }));
        searchItems({ ...state, ...stateChange });
    };

    const setEditDialogItemByIndex = (index: number) => {
        if (index < 0) {
            if (!state.prevPrediodLastItem) return;
            setState((old) => ({
                ...old,
                selectedId: state.prevPrediodLastItem.id,
                editDialogId: state.prevPrediodLastItem.id,
                editDialogStartDate: null
            }));
            calendarRef.current.moveToDate(moment.utc(state.prevPrediodLastItem.startDate));
        } else if (index >= state.items.length) {
            if (!state.nextPrediodFirstItem) return;
            setState((old) => ({
                ...old,
                selectedId: state.nextPrediodFirstItem.id,
                editDialogId: state.nextPrediodFirstItem.id,
                editDialogStartDate: null
            }));
            calendarRef.current.moveToDate(moment.utc(state.nextPrediodFirstItem.startDate));
        } else {
            setState((old) => ({
                ...old,
                selectedId: state.items[index].id,
                editDialogId: state.items[index].id,
                editDialogStartDate: null
            }));
        }
    };

    const handleMoveToPrevious = () => {
        if (!state.editDialogId) return;
        const index = state.items.findIndex(i => i.id === state.editDialogId);
        if (index < 0) return;
        setEditDialogItemByIndex(index - 1);
    };

    const handleMoveToNext = () => {
        if (!state.editDialogId) return;
        const index = state.items.findIndex(i => i.id === state.editDialogId);
        if (index < 0) return;
        setEditDialogItemByIndex(index + 1);
    };
    // #endregion Edit dialog

    const handleCalendarItemStartDate = async (id: string, startDate: number) => {
        const result = await AppUtils.callService(() => calendarTaskService.setCalendarTaskStartDate(props.companyId, id, startDate), false);
        if (!result) return;
        handleRefreshCalendar();
    };

    const handleCalendarItemRemove = async (id: string) => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveCalendarTaskParameter, state.items?.find(item => item.id === id)?.title), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => calendarTaskService.removeCalendarTask(props.companyId, id));
            if (!result) return;
        }
        handleRefreshCalendar();
    };

    const handleChangeFilter = (filter: string) => {
        searchItems({ ...state, filter });
    };

    return (
        <Box sx={{ display: "flex" }}>
            <div style={{ width: "100%", position: "relative", flexBasis: 0, flexGrow: 7, display: "flex" }}>
                <PageTitle title={Translations.Calendar + (props.savedFilter ? " - " + props.savedFilter.title : (props.viewFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))} position="absolute">
                    {props.settingsVisible
                        ? (
                            <SettingsButton
                                onClick={props.onToggleSettingsDialog}
                                sx={{ marginRight: "24px" }}
                            >
                                {Translations.Settings}
                            </SettingsButton>
                        )
                        : null}
                    <AddButton variant="contained" onClick={() => handleAddCalendarTask(null)}>
                        {Translations.AddCalendarTask}
                    </AddButton>
                    <PreviewToggleButton value="preview" selected={props.showDocumentPreview} onChange={() => props.onChangeShowDocumentPreview(!props.showDocumentPreview)} style={{ marginLeft: "24px", padding: "7px" }} />
                </PageTitle>
                <MainListContainer>
                    <ActionContainer
                        margin="1rem .5rem 1rem 1rem"
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <ListActionButtons filterIsSet={props.viewFilterIsSet} onFilter={props.onOpenFilterDrawer} />
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={handleChangeFilter}
                        />
                    </ListSearchContainer>
                    <Box sx={{ margin: "0 1rem 1rem 1rem" }}>
                        <Calendar
                            ref={calendarRef}
                            events={state.calendarItems}
                            onGetEvents={(startDate: number, endDate: number) => searchItems({
                                categoryIds: state.categoryIds,
                                states: state.states,
                                createUserCodes: state.createUserCodes,
                                handlerUserGroupCodes: state.handlerUserGroupCodes,
                                privacy: state.privacy,
                                filter: state.filter,
                                startDate,
                                endDate
                            })}
                            onDateClick={handleCalendarDateClick}
                            onEventClick={handleOpenCalendarItem}
                            onEventRemove={handleCalendarItemRemove}
                            onSetEventStartDate={handleCalendarItemStartDate}
                        />
                    </Box>
                </MainListContainer>
                {state.showEditDialog &&
                    <CalendarTaskEditDialog
                        isReadOnly={false}
                        companyId={props.companyId}
                        calendarTaskId={state.editDialogId}
                        recordId={null}
                        databaseRecordType={null}
                        startDate={state.editDialogStartDate}
                        sessionState={props.sessionState}
                        hasNext={state.editDialogHasNext}
                        hasPrevious={state.editDialogHasPrevious}
                        onRefreshCalendar={handleRefreshCalendar}
                        onPrevious={handleMoveToPrevious}
                        onNext={handleMoveToNext}
                        onSaved={handleOkEditDialog}
                        onClose={handleCloseEditDialog}
                        onNewSaved={handleNewSavedEditDialog}
                    />
                }
            </div>
            {props.showDocumentPreview &&
                <CalendarTaskList
                    startDate={state.startDate}
                    endDate={state.endDate}
                    events={state.items}
                    onItemClick={handleOpenCalendarItem}
                    onClose={() => props.onChangeShowDocumentPreview(false)}
                />
            }
        </Box>
    );
};
